import { useMemo, useRef, useState, useEffect } from 'react'

export interface OrgMember {
  uid: string
  email: string
  displayName?: string
}

export function memberAvatarColor(seed: string): string {
  const colors = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6']
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return colors[hash % colors.length]
}

export function MemberAvatar({ name, email }: { name?: string; email: string }) {
  const label = (name || email || '?').charAt(0).toUpperCase()
  return (
    <span
      className="avatar avatar-sm"
      style={{
        backgroundColor: memberAvatarColor(email),
        color: '#fff',
        border: 'none',
        flexShrink: 0,
        width: 28,
        height: 28,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        fontSize: 12,
        fontWeight: 600
      }}
    >
      {label}
    </span>
  )
}

interface MemberSearchAddProps {
  members: OrgMember[]
  excludeUids: string[]
  onAdd: (member: OrgMember) => void
  placeholder?: string
}

/**
 * Notionライクなメンバー検索・追加入力欄。
 * 入力すると組織メンバーを名前/メールでインクリメンタル検索し、
 * 選択するとアクセスリストに追加される。
 */
export function MemberSearchAdd({ members, excludeUids, onAdd, placeholder }: MemberSearchAddProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase()
    return members
      .filter(m => !excludeUids.includes(m.uid))
      .filter(m =>
        !q ||
        (m.displayName || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
      )
      .slice(0, 8)
  }, [members, excludeUids, query])

  return (
    <div ref={containerRef} className="relative" style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || 'メンバーを検索して追加'}
        className="form-input"
        style={{ width: '100%' }}
      />
      {open && candidates.length > 0 && (
        <div
          className="dropdown-menu open"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            maxHeight: 240,
            overflowY: 'auto',
            marginTop: 4
          }}
        >
          {candidates.map((m) => (
            <div
              key={m.uid}
              className="dropdown-item"
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
              onClick={() => {
                onAdd(m)
                setQuery('')
                setOpen(false)
              }}
            >
              <MemberAvatar name={m.displayName} email={m.email} />
              <div style={{ minWidth: 0 }}>
                <div className="text-sm font-medium text-primary truncate">{m.displayName || m.email}</div>
                <div className="text-xs text-tertiary truncate">{m.email}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {open && query.trim() && candidates.length === 0 && (
        <div
          className="dropdown-menu open"
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4 }}
        >
          <div className="dropdown-item text-sm text-tertiary" style={{ cursor: 'default' }}>
            該当するメンバーが見つかりません
          </div>
        </div>
      )}
    </div>
  )
}
